import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { mailboxesApi } from '@/features/mailboxes/api';
import { apiErrorMessage } from '@/api/errors';
import type { MailboxListParams } from '@/features/mailboxes/types';

const keys = {
  all: ['mailboxes'] as const,
  list: (p: MailboxListParams) => ['mailboxes', 'list', p] as const,
  detail: (id: string) => ['mailboxes', 'detail', id] as const,
  domainSummary: ['mailboxes', 'domain-summary'] as const,
};
export const useMailboxesList = (p: MailboxListParams) =>
  useQuery({ queryKey: keys.list(p), queryFn: () => mailboxesApi.list(p), placeholderData: (x) => x });
export const useMailboxDomainSummary = () =>
  useQuery({ queryKey: keys.domainSummary, queryFn: mailboxesApi.domainSummary, staleTime: 30_000 });
export const useMailbox = (id: string) =>
  useQuery({ queryKey: keys.detail(id), queryFn: () => mailboxesApi.get(id), enabled: !!id });

export function useCreateMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: mailboxesApi.create,
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); notify.success('Mailbox created'); },
    // No toast: the create dialog shows every failure inline (see createMailboxError).
  });
}
export function useUpdateMailbox(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: Partial<{ displayName: string; status: string; quotaBytes: number }>) => mailboxesApi.update(id, b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); qc.invalidateQueries({ queryKey: keys.detail(id) }); notify.success('Mailbox updated'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not update mailbox')),
  });
}
export function useDeleteMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mailboxesApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); notify.success('Mailbox deleted'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not delete mailbox')),
  });
}
export function useResetMailboxPassword(id: string) {
  return useMutation({
    mutationFn: (b: { newPassword: string; revokeSessions?: boolean }) => mailboxesApi.resetPassword(id, b),
    onSuccess: () => notify.success('Password reset'),
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not reset password')),
  });
}
